import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SettingsIcon, SunIcon, Volume2Icon } from 'lucide-react';
const rooms = ['Living', 'Theatre', 'Bedroom', 'Terrace'];
const scenes = [{
  name: 'Morning',
  color: 'social',
  hex: '#F59E0B'
}, {
  name: 'Movie Night',
  color: 'cinema',
  hex: '#6366F1'
}, {
  name: 'Party',
  color: 'music',
  hex: '#EC4899'
}, {
  name: 'Goodnight',
  color: 'relax',
  hex: '#10B981'
}];
export function ControlDemo() {
  const [activeRoom, setActiveRoom] = useState('Living');
  const [activeScene, setActiveScene] = useState(scenes[0]);
  const [volume, setVolume] = useState(40);
  const [light, setLight] = useState(70);
  const handleSceneClick = (scene: (typeof scenes)[0]) => {
    setActiveScene(scene);
    if (scene.name === 'Movie Night') {
      setVolume(65);
      setLight(10);
    }
    if (scene.name === 'Morning') {
      setVolume(20);
      setLight(80);
    }
    if (scene.name === 'Party') {
      setVolume(85);
      setLight(100);
    }
    if (scene.name === 'Goodnight') {
      setVolume(0);
      setLight(0);
    }
  };
  return <section id="control" className="py-32 bg-void relative border-t border-graphite" data-id="element-228">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col lg:flex-row items-center gap-16" data-id="element-229">
        <div className="w-full lg:w-1/2" data-id="element-230">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-231">
            <h2 className="text-headline font-serif text-platinum mb-6" data-id="element-232">
              Command Every Moment
            </h2>
            <p className="text-silver mb-8 text-lg" data-id="element-233">
              One elegant interface to orchestrate your entire environment. No
              clutter, no confusion. Just pure control.
            </p>

            <ul className="space-y-6" data-id="element-234">
              {[{
              title: 'Unified Architecture',
              desc: 'Audio, video, climate, and lighting in one app.'
            }, {
              title: 'Contextual Intelligence',
              desc: 'The interface adapts to the room you are in.'
            }, {
              title: 'One-Touch Scenes',
              desc: 'Transform the mood instantly with curated presets.'
            }].map((item, i) => <li key={i} className="flex flex-col" data-id="element-235">
                  <span className="text-platinum font-serif text-xl mb-1" data-id="element-236">
                    {item.title}
                  </span>
                  <span className="text-silver text-sm" data-id="element-237">{item.desc}</span>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* iPad Mockup */}
        <div className="w-full lg:w-1/2 flex justify-center relative" data-id="element-238">
          {/* Ambient Glow behind iPad */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 rounded-full blur-[100px] opacity-20 transition-colors duration-1000" style={{
          backgroundColor: activeScene.hex
        }} data-id="element-239" />

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="w-full max-w-md bg-steel p-3 rounded-[2.5rem] shadow-2xl relative z-10" data-id="element-240">
            {/* Screen */}
            <div className="bg-obsidian rounded-[2rem] overflow-hidden h-[650px] flex flex-col relative border border-carbon" data-id="element-241">
              {/* Top Bar */}
              <div className="px-6 pt-6 pb-4 flex justify-between items-center" data-id="element-242">
                <span className="text-platinum font-medium" data-id="element-243">9:41</span>
                <span className="font-serif text-platinum tracking-widest text-sm" data-id="element-244">
                  QUBIX
                </span>
                <SettingsIcon className="w-5 h-5 text-silver" data-id="element-245" />
              </div>

              {/* Room Tabs */}
              <div className="px-4 mb-6 flex space-x-2 overflow-x-auto no-scrollbar" data-id="element-246">
                {rooms.map(room => <button key={room} onClick={() => setActiveRoom(room)} className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${activeRoom === room ? 'bg-carbon text-platinum border border-graphite' : 'text-silver hover:text-platinum'}`} data-id="element-247">
                    {room}
                  </button>)}
              </div>

              {/* Main Content */}
              <div className="flex-1 px-6 overflow-y-auto pb-6 space-y-8" data-id="element-248">
                <motion.div key={activeRoom} initial={{
                opacity: 0,
                x: 10
              }} animate={{
                opacity: 1,
                x: 0
              }} className="space-y-8" data-id="element-249">
                  <h3 className="text-3xl font-serif text-platinum" data-id="element-250">
                    {activeRoom}
                  </h3>

                  {/* Scenes */}
                  <div data-id="element-251">
                    <h4 className="text-xs uppercase tracking-widest text-silver mb-3" data-id="element-252">
                      Scenes
                    </h4>
                    <div className="grid grid-cols-2 gap-3" data-id="element-253">
                      {scenes.map(scene => {
                      const isActive = activeScene.name === scene.name;
                      return <button key={scene.name} onClick={() => handleSceneClick(scene)} className={`p-4 rounded-xl text-left transition-all border-l-4 ${isActive ? `bg-${scene.color}/10 border-${scene.color} text-${scene.color}` : `bg-carbon border-transparent text-platinum hover:border-graphite`}`} style={isActive ? {
                        borderColor: scene.hex,
                        color: scene.hex,
                        backgroundColor: `${scene.hex}1A`
                      } : {
                        borderLeftColor: scene.hex
                      }} data-id="element-254">
                            {scene.name}
                          </button>;
                    })}
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="space-y-6" data-id="element-255">
                    {/* Lighting */}
                    <div className="bg-carbon p-5 rounded-2xl border border-graphite" data-id="element-256">
                      <div className="flex justify-between items-center mb-4" data-id="element-257">
                        <div className="flex items-center text-platinum" data-id="element-258">
                          <SunIcon className="w-5 h-5 mr-2" style={{
                          color: activeScene.hex
                        }} data-id="element-259" />
                          <span data-id="element-260">Lighting</span>
                        </div>
                        <span className="text-silver text-sm" data-id="element-261">{light}%</span>
                      </div>
                      <div className="w-full h-2 bg-obsidian rounded-full overflow-hidden" data-id="element-262">
                        <div className="h-full transition-all duration-300" style={{
                        width: `${light}%`,
                        backgroundColor: activeScene.hex
                      }} data-id="element-263" />
                      </div>
                    </div>

                    {/* Audio */}
                    <div className="bg-carbon p-5 rounded-2xl border border-graphite" data-id="element-264">
                      <div className="flex justify-between items-center mb-4" data-id="element-265">
                        <div className="flex items-center text-platinum" data-id="element-266">
                          <Volume2Icon className="w-5 h-5 mr-2" style={{
                          color: activeScene.hex
                        }} data-id="element-267" />
                          <span data-id="element-268">Audio</span>
                        </div>
                        <span className="text-silver text-sm" data-id="element-269">{volume}%</span>
                      </div>
                      <div className="w-full h-2 bg-obsidian rounded-full overflow-hidden" data-id="element-270">
                        <div className="h-full transition-all duration-300" style={{
                        width: `${volume}%`,
                        backgroundColor: activeScene.hex
                      }} data-id="element-271" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}